import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface AuthEvent {
  id: string
  event_type: string
  email: string | null
  user_id: string | null
  ip_address: string | null
  user_agent: string | null
  metadata: Record<string, unknown> | null
  created_at: number // timestamp ms
}

export interface AuthEventsStats {
  loginsToday: number
  failuresToday: number
  uniqueUsersWeek: number
  recentFailures: AuthEvent[]
  lastLogin: AuthEvent | null
}

const keys = {
  all: ['auth-events'] as const,
  list: (params: { limit: number; offset: number; type?: string; email?: string }) =>
    [...keys.all, 'list', params] as const,
  stats: () => [...keys.all, 'stats'] as const,
}

export function useAuthEvents(params: {
  limit?: number
  offset?: number
  type?: string
  email?: string
} = {}) {
  const { limit = 50, offset = 0, type, email } = params

  return useQuery({
    queryKey: keys.list({ limit, offset, type, email }),
    queryFn: async () => {
      const searchParams: Record<string, string | number> = { limit, offset }
      if (type) searchParams['type'] = type
      if (email) searchParams['email'] = email

      return apiClient.get<{
        events: AuthEvent[]
        total: number
        limit: number
        offset: number
      }>('/api/auth-events', { params: searchParams })
    },
  })
}

export function useAuthEventsStats() {
  return useQuery({
    queryKey: keys.stats(),
    queryFn: () => apiClient.get<AuthEventsStats>('/api/auth-events/stats'),
    refetchInterval: 60_000, // Refresh every minute
  })
}

export const EVENT_TYPE_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  login_success: { label: 'Login', variant: 'default' },
  login_failure: { label: 'Failed', variant: 'destructive' },
  domain_rejected: { label: 'Domain Rejected', variant: 'destructive' },
  callback_error: { label: 'Callback Error', variant: 'destructive' },
  token_invalid: { label: 'Invalid Token', variant: 'secondary' },
  session_expired: { label: 'Session Expired', variant: 'outline' },
  health_check_failure: { label: 'Health Check', variant: 'secondary' },
}
